import React, { useEffect, useState } from "react";
import { fetchSettings } from "@/lib/api";
import { Coins } from "lucide-react";

/**
 * Slim trust-building strip showing today's gold/silver rates, as set by
 * the admin. Renders nothing if no rates have been configured yet, so it
 * never shows an awkward "N/A" placeholder to visitors.
 */
export const RateTicker = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchSettings();
        if (!cancelled) setSettings(data);
      } catch {
        if (!cancelled) setSettings(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!settings) return null;

  const rates = [
    settings.gold_rate_24k && { label: "24K Gold", value: `₹${settings.gold_rate_24k.toLocaleString("en-IN")}/g` },
    settings.gold_rate_22k && { label: "22K Gold", value: `₹${settings.gold_rate_22k.toLocaleString("en-IN")}/g` },
    settings.gold_rate_18k && { label: "18K Gold", value: `₹${settings.gold_rate_18k.toLocaleString("en-IN")}/g` },
    settings.silver_rate_per_gram && { label: "Silver", value: `₹${settings.silver_rate_per_gram.toLocaleString("en-IN")}/g` },
  ].filter(Boolean);

  if (rates.length === 0) return null;

  const updatedDate = settings.updated_at ? new Date(settings.updated_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : null;

  return (
    <div className="bg-card border-b border-border" data-testid="rate-ticker">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex flex-wrap items-center justify-center gap-x-6 gap-y-1.5 text-xs sm:text-sm">
        <span className="flex items-center gap-1.5 text-primary font-medium">
          <Coins className="h-3.5 w-3.5" /> Today&apos;s Rate{updatedDate ? ` (${updatedDate})` : ""}:
        </span>
        {rates.map((r) => (
          <span key={r.label} className="text-foreground/85">
            <span className="text-muted-foreground">{r.label}</span> <span className="font-semibold">{r.value}</span>
          </span>
        ))}
      </div>
    </div>
  );
};
